import type { FilterItem, FilterItemPascal, keyPair } from "@repo/models";

import { KeyedArray, mapToKeyedArray, mapToKeyedArrayPascal } from "@repo/models";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

const emptyArray = new KeyedArray<FilterItemPascal>([], (k) => k.Id);

// After the first fetch, keeps returning the last fetched list (and loaded=true) while a new key loads.
function useKeyedArrayQuery<T>(
	queryKey: unknown[],
	fetchItems: () => Promise<KeyedArray<T>>,
): [KeyedArray<T>, boolean] {
	const { data, isFetched } = useQuery({ queryKey, queryFn: fetchItems });
	const lastFetched = useRef({ data: data ?? emptyArray, loaded: isFetched });

	useEffect(() => {
		if (isFetched) lastFetched.current = { data, loaded: true };
	}, [isFetched, data]);

	if (isFetched) return [data, true];
	return [lastFetched.current.data as KeyedArray<T>, lastFetched.current.loaded];
}

export function useFilterItemQuery<T extends FilterItem = FilterItem>(
	name: string[],
	api: (...params) => Promise<(T | FilterItem)[]>,
	...params
) {
	const fetchItems = async () => api(...params).then(mapToKeyedArray);
	return useKeyedArrayQuery([...name, ...params], fetchItems) as [KeyedArray<T>, boolean];
}

export function useFilterItemPascalQuery<T extends FilterItemPascal = FilterItemPascal>(
	name: string[],
	api: (...params) => Promise<(T | FilterItemPascal)[]>,
	...params
) {
	const fetchItems = async () => api(...params).then(mapToKeyedArrayPascal);
	return useKeyedArrayQuery([...name, ...params], fetchItems) as [KeyedArray<T>, boolean];
}

function toKeyPairs<T>(
	allItems: KeyedArray<T>,
	loaded: boolean,
	forceInclude: T | undefined,
	toPair: (item: T) => keyPair,
) {
	if (!loaded) return null as keyPair[];
	if (!allItems) return [];
	if (forceInclude) allItems.set(forceInclude);
	return allItems.map((item) => toPair(item));
}

export function getItems(allItems: KeyedArray<FilterItem>, loaded: boolean, forceInclude?: FilterItem) {
	return toKeyPairs(allItems, loaded, forceInclude, (c) => ({ id: c.id, value: c.name ?? c.value }));
}

export function getItemsPascal(
	allItems: KeyedArray<FilterItemPascal>,
	loaded: boolean,
	forceInclude?: FilterItemPascal,
) {
	return toKeyPairs(allItems, loaded, forceInclude, (c) => ({ id: c.Id, value: c.Name }));
}
